/* ── ⚠⚠ v16.7 W1 (F1): THE NUMBERING REPAIRS A CLASH, IT NO LONGER ONLY FILLS BLANKS ──────────
   §v16.6 P1 REPRODUCED what the old comment here called impossible. That comment claimed "array
   order is identical on every machine because every machine parsed the same bytes, so every machine
   mints the same numbers and the collision cannot arise" — and its PREMISE is what fails: a syncing
   folder does not guarantee that every machine can see every colleague's part. Three colleagues
   upgrading at three ordinary moments gave the SAME programme three different ids, and **39% of 180
   simulated portfolios settled with two records sharing one FOR EVER** — because the old pass only
   ever filled an ABSENT number, so nothing in the app ever disagreed with a wrong one already
   written. That made it a one-way door.
   ⚠ THE FIX IS A RULE CHANGE, NOT A ONE-OFF REPAIR, and that distinction matters: this runs on
   EVERY open, on every machine, exactly as before. What changes is that it now also RESOLVES a
   clash instead of preserving it.
     1. A number that exists NEVER MOVES on its own — ◆d-1 stands, an id is for quoting.
     2. If two live records share one, the winner is chosen by the SAME rule on every machine:
        the record whose MERGE KEY sorts first keeps it (`_mergeCmpKey`, the very comparator the
        merge itself orders by, so this cannot disagree with the portfolio the user sees). The
        loser's number is cleared and re-minted.
     3. Anything without a number is minted from max+1, in merged order.
   ⚠ WHY THAT CONVERGES: every step is a pure function of the merged portfolio, so two machines
   holding the same parts compute the same answer. Machines holding DIFFERENT parts still differ
   transiently — that is unavoidable without a server — but they can no longer settle WRONG, which
   is the guarantee he accepted ("cannot end up wrong", not "never briefly inconsistent").
   ⚠ SILENT, like every other heal here: no markDirty. Memory is correct immediately and the
   corrected numbers land on whatever Save was coming anyway; the file is never rewritten to force
   it. Idempotent, so a clean portfolio is untouched and roundtrip_byte_exact does not move.
   ✅ F3 (a deleted record's number handed to the next new one) WAS the carve-out here and is
   FIXED in v16.9 (W4), on his explicit schema consent of 2026-08-28 given separately from the
   Go. A deletion marker now carries `displayNo`, and the mint below counts markers as well as
   live records. ⚠ The paragraph that used to sit here said F3 "needs stored state that does not
   exist"; that was true until this version and is kept as history rather than instruction.
   ⚠⚠ WHAT THE FIX DOES NOT DO, measured in v16.9 W2 and stated so nobody re-discovers it:
   numbers freed BEFORE this shipped stay freed (the app cannot know what it never recorded);
   a programme re-created under a name that NORMALISES to the dead one's key still reclaims it;
   deleting and re-creating BEFORE a save produces no marker at all; a colleague's project under
   a programme deleted elsewhere is dropped by the cascade with its number; a marker's number
   survives a merge only when its record wins outright; and an imported copy may carry the
   number it had in the file it came from. See §v16.9's W2 census. */
function _normDisplayNumbers(db) {
  if (!db || typeof db !== 'object') return;
  const progs = Array.isArray(db.programmes) ? db.programmes : [];
  const projs = Array.isArray(db.projects) ? db.projects : [];
  [[progs, 'id'], [projs, 'uid']].forEach(pair => {
    const list = pair[0], keyField = pair[1];
    /* Tombstones are never numbered and never contest a number. */
    const live = list.filter(r => r && typeof r === 'object' && !r.del);
    /* 1. Repair: a number held by more than one live record. */
    const held = new Map();
    live.forEach(r => {
      const n = _dispNoOf(r);
      if (!n) return;
      if (!held.has(n)) held.set(n, []);
      held.get(n).push(r);
    });
    held.forEach(group => {
      if (group.length < 2) return;
      /* ⚠ The tie-break must be a TOTAL order every machine computes identically, or two
         machines would keep handing the number to different records and never settle. */
      group.sort((a, b) => _mergeCmpKey(String(a[keyField]), String(b[keyField])));
      for (let i = 1; i < group.length; i++) delete group[i].displayNo;
    });
    /* 2. Mint what has no number, from above the highest still held.
       ⚠⚠ v16.9 (W4, F3): `list`, NOT `live` - AND THAT ONE WORD IS THE ENTIRE READ-SIDE FIX.
       `list` is live records AND deletion markers, so the high-water mark now counts a number
       whose record is gone. _dispNoNext's arithmetic is untouched: it was always a plain max+1
       over whatever it is handed, so changing it would have achieved nothing.
       ⚠⚠ THE MARKERS ARE IN REACH AT THIS INSTANT, and only at this instant: _normProgrammes
       runs BEFORE _revAdoptBaseline harvests them out of the arrays (v16.7 W3's fact), and this
       is the ONLY caller of this function. ⛔ Do not move the call, and do not make this read a
       GLOBAL tomb store instead: v16.9 W2's L7 MEASURED that _revTombs still holds the PREVIOUS
       portfolio's markers while a new one is being normalised, so a global lookup here would
       reserve numbers out of the wrong portfolio.
       ⚠⚠ AND IT MUST NOT REACH THE REPAIR STEP ABOVE. If a marker's number counted as "held"
       there, a LIVE record legitimately carrying that same number - normal for anything numbered
       before this shipped - would have its own number deleted and be re-minted. That MOVES a
       number on a live record, which is precisely the harm F3 exists to prevent. The repair's
       comment stays true: tombstones never contest a number. Only the mint's start moved. */
    let next = _dispNoNext(list);
    live.forEach(r => { if (!_dispNoOf(r)) r.displayNo = next++; });
  });
}
/* v16.5 (d): the label a person reads and quotes. Derived from the STORED number, so the
   prefix and padding can change without touching a byte of anyone's data. */
function _dispId(prefix, rec) {
  const n = (rec && typeof rec.displayNo === 'number' && isFinite(rec.displayNo)) ? rec.displayNo : 0;
  return n > 0 ? (prefix + '-' + String(n).padStart(2, '0')) : '';
}
/* ── ⚠⚠ v16.5 (f): THE PROGRAMME'S LIFECYCLE STATUS ────────────────────────────────────────
   His ask was "stage-gates ... but at program level, and not the same states", and then, when
   the first two explanations missed the point: *"It's simply a field for management to be able
   to gauge and for program managers to manually set that gauge. It shouldn't modify any other
   data or statuses."* HE WAS RIGHT AND THERE WAS NEVER A DISAGREEMENT ABOUT THE FEATURE.
   ⚠⚠ IT IS A STATUS, NOT A GATE STRIP, AND THE DIFFERENCE IS NOT COSMETIC. A gate strip is a
   PROGRESSION — everything to the left of where you are turns green as "passed". Lay these five
   states in a row and mark a programme ON HOLD, and the strip's own rules light up COMPLETED as
   passed: the screen would claim a programme finished when it is paused. On hold and cancelled
   are not further along than in progress; they can happen from any point and have nothing behind
   them. So: one label at a time.
   ⚠ IT MODIFIES NOTHING. No derivation, no cascade to projects, no effect on rag, no gating of
   any other control. A gauge, read and set by a human — exactly as he specified.
   ⚠ ORTHOGONAL TO rag, deliberately: RAG is HEALTH, lifecycle is WHERE IN ITS LIFE. A programme
   can legitimately be green and on hold, and a check proves that stays expressible — if the two
   ever collapse into one control, that check goes red.
   ⚠ LAZY: absent until set, so a file that never uses it round-trips byte-identically. */
function _progLifecycleLabel(v) {
  const hit = PROG_LIFECYCLE.find(x => x[0] === v);
  return hit ? hit[1] : '';
}
function setProgrammeLifecycle(progId, value) {
  const P = _programmes().find(x => x && x.id === progId);
  if (!P) return;
  /* clearing DELETES the key rather than writing a falsy one - the lazy-key rule this repo
     uses for archived / allocationPct / plannedStart / noWbs, so an unset programme is
     byte-identical to one written before the field existed. */
  if (PROG_LIFECYCLE.some(x => x[0] === value)) P.lifecycle = value; else delete P.lifecycle;
  markDirty();
  renderPortfolio();
  if (typeof renderOverview === 'function') renderOverview();
}
function _normBenefit(b) {
  if (typeof b.name !== 'string') b.name = '';
  ['measure', 'baseline', 'target', 'current', 'owner'].forEach(k => { if (typeof b[k] !== 'string') b[k] = ''; });
  if (!_BEN_STATUS.some(s => s[0] === b.status)) b.status = null;
  if (!Array.isArray(b.projectIds)) b.projectIds = [];
  b.projectIds = b.projectIds.filter(x => typeof x === 'string');
}
function _progBenefits() {
  const P = _activeProg();
  return (P && Array.isArray(P.benefits)) ? P.benefits : [];
}
/* ── the programme's NAME (v10.5, changed to EAGER in v11) ────────────────────────────
   In v10.5 `name` was a deliberately LAZY key: the singular branch had to keep
   round-tripping byte-exactly for files written before the key existed, so normalise
   never backfilled it and clearing a name DELETED the key.
   ⚠ v11 reverses that decision, deliberately: `programmes[]` is a NEW shape with no
   pre-existing files to stay byte-compatible with (◆A removed backward compatibility
   outright), and `createProgramme`/`_normProgrammes` therefore always carry `name` as a
   string. Eager is simpler and there is no longer anything to protect. Build standard 6
   asks that lazy-vs-eager be a decision with its round-trip consequence stated — this is
   that decision, restated for v11 rather than inherited by accident. */
function _progName() {
  const P = _activeProg();
  return (P && typeof P.name === 'string') ? P.name.trim() : '';
}
function _setProgName(v) {
  const P = _activeProg();
  if (!P) return false;
  const next = (v || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  const prev = (typeof P.name === 'string') ? P.name : '';
  if (next === prev) return false;
  P.name = next;                                 /* eager in v11 — see the note above */
  return true;
}
/* ⚠ The v10.5 fallback showed the FILE name for an unnamed programme, because there the
   file WAS the programme and its name was real provenance. Under v11 the file is the
   whole portfolio, so its name says nothing about any one programme — showing it would be
   actively misleading. State the absence instead, muted, with a prompt to fix it. */
function _progLabel() {
  const n = _progName();
  return n ? { text: n, fallback: false } : { text: 'Unnamed programme', fallback: true };
}
/* v10.5 (◆2): false until the boot handle-restore has settled. Before that, any name we
   could show is the SIBLING BOOTSTRAP file's, not the bound programme's — so the title
   is held blank rather than flashing the wrong programme. On file:// there is no
   restore chain, so nothing to wait for. */
let _progTitleReady = (location.protocol === 'file:');
function _progDocTitle() {
  const n = _progName();
  return (n ? n + ' — ' : '') + 'TrendAI FlightDeck';
}
/* The programme crumb on the project view. Muted-italic when it is standing in the
   file name for an unnamed programme, so the trail never asserts a name that is not
   there. */
function _renderProgCrumb() {
  const el = document.getElementById('crumbProgName');
  if (!el) return;
  const lab = _progLabel();
  el.textContent = lab.text;
  el.classList.toggle('crumb-unnamed', lab.fallback);
  el.title = lab.fallback
    /* v13.1 (A31): "showing its file" is v10.5 vocabulary — a programme WAS a file then.
       Since v11 it is a record inside the one portfolio file, and there is no file name on
       screen for the user to relate this to. */
    ? 'This programme has no name yet. Open the programme to name it.'
    : 'Back to all projects in this programme';
}
/* v17.0 W1: a single renderer owns the durable location rail in both contexts.  It
   changes content and current-location emphasis only; the slot boxes themselves never
   move.  The Focus return lives in the utility slot, outside the ancestry. */
function _renderLocationRail(level) {
  const portfolio = document.getElementById('crumbPortfolioName');
  if (portfolio) portfolio.classList.toggle('is-current', level === 'portfolio');
  _renderProgCrumb();
  const programme = document.getElementById('crumbProgName');
  if (programme) programme.classList.toggle('is-current', level === 'programme');
  const project = ((window.__DB__ && window.__DB__.projects) || [])
    .find(x => x && x.id === activeProjectId);
  const name = document.getElementById('crumbProjName');
  if (name) name.textContent = project ? (project.name || 'Project') : 'Project';
  _paintCrumbPredecessors(level === 'project' ? project : null);
  const projectButton = document.querySelector('#crumbProjectSlot .crumb-proj-btn');
  if (projectButton) projectButton.classList.toggle('is-current', level === 'project');
  const fromFocus = level === 'project' && _projReturn === 'focus';
  const focus = document.getElementById('crumbFocusBack');
  if (focus) focus.hidden = !fromFocus;
  const legacySep = document.getElementById('crumbFocusSep');
  if (legacySep) legacySep.hidden = true;
}
/* v10.5 (W1): rename the programme. Deliberately the SAME click-to-edit gesture as a
   project name (handleProjectEdit) — one gesture at both levels, which is what made the
   title-and-trail rule worth having. Clearing it deletes the key and falls back to the
   file name rather than inventing "Untitled programme". */
/* ── v12.3.1: do not swallow the click that is on its way somewhere else ─────────────
   `renderOverview()` below replaces the whole subtree, and since v12.3's W2 the manager
   field lives INSIDE it. So a click travelling from this field to that one blurs us, we
   re-render, and the node the click was heading for no longer exists — the first click is
   lost and only the second lands. The blur event knows where focus was going
   (`relatedTarget`), so capture its ID *before* the re-render and restore focus after.
   ⚠ The ID must be read BEFORE renderOverview(): after it, the captured node is detached
   and focusing it would put the caret in a subtree that is no longer on screen.
   ⚠ Only reached on a REAL rename — `_setProgName` returns false when the name is
   unchanged, which early-returns above without re-rendering (so nothing is ever lost in
   that case, and a QA check must actually type a new name to see this at all). */
function handleProgrammeNameEdit(el, ev) {
  if (!_setProgName(el.textContent || '')) {
    const lab = _progLabel(); el.textContent = lab.text;   /* whitespace-only edit — put it back */
    return;
  }
  markDirty();
  document.title = _progDocTitle();
  const destination = ev && ev.relatedTarget;
  if (destination && destination.classList
      && destination.classList.contains('list-order-select')) {
    destination.addEventListener('blur', function () { renderOverview(); }, { once: true });
    return;
  }
  if (_listOrderIsInteractionTarget(destination)) {
    _listOrderDeferEditorRepaint(destination, renderOverview);
    return;
  }
  const toId = (ev && ev.relatedTarget && ev.relatedTarget.id) || '';
  renderOverview();
  _refocusAfterRender(toId);
}
/* Focus the element the click was heading for, now that the re-render has rebuilt it.
   ⚠ Caret placement matters and is deliberate: a bare focus() on a contenteditable lands
   the caret at position 0, so typing into the em-dash placeholder would produce "—Anna"
   BY DEFAULT — a worse defect than the one this fixes. So the placeholder is SELECTED
   (typing replaces it) and real content gets the caret at the END (typing appends).
   Written as a helper because it fixes the CLASS of problem: any focusable element inside
   a re-rendered view. Deliberately only ONE caller in this patch. */
function _refocusAfterRender(toId) {
  if (!toId) return;                       /* Enter, or a click on nothing focusable */
  const t = document.getElementById(toId);
  if (!t) return;                          /* the target genuinely went away */
  t.focus();
  if (!t.isContentEditable) return;
  const r = document.createRange();
  r.selectNodeContents(t);
  if ((t.textContent || '').trim() !== '—') r.collapse(false);   /* caret to the end */
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(r);                         /* placeholder stays selected, so typing replaces */
}
function _nextBenSeq() {
  const P = _activeProg(); if (!P) return 1;
  const n = _mintNextId(_progBenefits(), _myWriterId(), P.benSeq);   /* v14 (W3b) */
  P.benSeq = n;   /* lazy — first use creates it; never reused after the highest is deleted */
  return n;
}
function _benRegId(b) { return 'BEN-' + String(_mintDisplayOrd(_progBenefits(), b)).padStart(3, '0'); }   /* v14 (W3b): derived */
if (window.__DB__ && typeof window.__DB__ === 'object' && window.__DB__.schemaVersion === SCHEMA_VERSION) {
  _normProgrammes();   /* module-load pass (the RISKS.forEach precedent); schema-gated */
}

function cycleProgrammeRag() {
  const P = _activeProg(); if (!P) return;
  let i = _RAG_CYCLE.indexOf(P.rag.level); if (i < 0) i = 0;
  P.rag.level = _RAG_CYCLE[(i + 1) % _RAG_CYCLE.length];
  markDirty();
  _renderProgrammeRag();
}
function _renderProgrammeRag() {
  const pill = document.getElementById('pmRagPill'); if (!pill) return;
  const _pa = _activeProg(); if (!_pa) return;
  const lvl = _pa.rag.level;
  pill.classList.remove('rag-red', 'rag-amber', 'rag-green', 'rag-none');
  pill.classList.add(_ragClassFor(lvl));
  pill.textContent = (lvl === null) ? 'Not set' : _RAG_LABEL[lvl];
}
function addProgrammeBenefit() {
  const P = _activeProg(); if (!P) return;
  if (!Array.isArray(P.benefits)) P.benefits = [];
  const b = { id: _nextBenSeq(), name: '', measure: '', baseline: '', target: '', current: '', status: null, owner: '', projectIds: [] };
  _normBenefit(b);
  P.benefits.push(b);
  markDirty();
  _renderProgrammeBenefits();
  const row = document.querySelector(`#ovBenefitsList .pb-card[data-id="${b.id}"]`);
  if (row) { row.scrollIntoView({ block: 'center' }); const n = row.querySelector('.pb-name'); if (n) n.focus(); }
}
function deleteProgrammeBenefit(id) {
  const arr = _progBenefits();
  const idx = arr.findIndex(b => b.id === id);
  if (idx < 0) return;
  const b = arr[idx];
  const label = (b.name && b.name.trim()) ? '"' + _escapeHtml(b.name.trim()) + '"' : 'this benefit';
  showConfirm('Delete benefit?',
    'This will remove ' + label + ' (' + _escapeHtml(_benRegId(b)) + ') from the programme.<br><br>You\'ll have 6 seconds to undo.',
    () => {
      const list = _progBenefits();
      const i2 = list.findIndex(x => x.id === id);
      if (i2 < 0) return;
      const removed = list.splice(i2, 1)[0]; const at = i2;
      /* ── v13.1 (A03): capture the OWNING programme's id by value. This closure used to
         resolve its destination through _progBenefits(), i.e. lazily, into whichever
         programme happened to be active when Undo was clicked — so deleting a benefit in A,
         going back to the portfolio, opening B and clicking Undo put A's benefit into B,
         at A's old index, keeping A's BEN-nnn number (which can collide with one of B's,
         because benSeq is per programme). A permanently lost it; B silently gained it; the
         toast said "Benefit restored". Reproduced in the v13 review. Resolve by ID so the
         restore cannot be aimed by the current view. */
      const _srcProgId = (typeof activeProgrammeId !== 'undefined') ? activeProgrammeId : null;
      markDirty(); _renderProgrammeBenefits();
      showUndoToast('Benefit deleted', () => {
        const _P = (_programmes() || []).find(p => p && p.id === _srcProgId);
        if (!_P) { showToast('That programme is no longer here, so the benefit was not restored.', { type: 'warn' }); return; }
        if (!Array.isArray(_P.benefits)) _P.benefits = [];
        _P.benefits.splice(at, 0, removed);
        markDirty(); _renderProgrammeBenefits();
        showToast('Benefit restored', { type: 'info' });
      }, 'portfolio');   /* v15: anchored by _srcProgId — A03's whole point; not project-scoped */
    });
}
function cycleProgrammeBenefitStatus(id) {
  const b = _progBenefits().find(x => x.id === id); if (!b) return;
  const order = _BEN_STATUS.map(x => x[0]);
  let i = order.indexOf(b.status); if (i < 0) i = 0;
  b.status = order[(i + 1) % order.length];
  markDirty(); _renderProgrammeBenefits();
}
function _renderProgrammeBenefits() {
  const host = document.getElementById('ovBenefitsList');
  if (!host) return;
  const list = _progBenefits();
  host.innerHTML = '';
  const cEl = document.getElementById('ovBenefitsCount');
  if (cEl) cEl.textContent = list.length ? String(list.length) : '';
  const empty = document.getElementById('ovBenefitsEmpty');
  if (empty) empty.style.display = list.length ? 'none' : '';
  list.forEach(b => host.appendChild(_buildBenefitCard(b)));
}
function _buildBenefitCard(b) {
  const card = document.createElement('div');
  card.className = 'assumption-card pb-card';
  card.dataset.id = b.id;
  const top = document.createElement('div');
  top.className = 'as-top';
  const m = _benStatusMeta(b.status);
  top.innerHTML =
    `<span class="as-regid" title="Benefit id">${esc(_benRegId(b))}</span>` +
    `<span class="pb-name" contenteditable="true" spellcheck="false" data-ph="Benefit — the measurable outcome…"></span>` +
    `<span class="as-spacer"></span>` +
    `<span class="ben-status ${m[2]}" title="Benefit status — click to cycle">${esc(m[1])}</span>` +
    `<span class="rc-card-actions"><button class="rc-delete" title="Delete benefit">×</button></span>`;
  const nameEl = top.querySelector('.pb-name');
  nameEl.textContent = b.name || '';
  _onBlurCommit(nameEl, () => {
    const v = (nameEl.textContent || '').trim();
    if (v !== (b.name || '')) { b.name = v; markDirty(); }
  });
  top.querySelector('.ben-status').addEventListener('click', () => cycleProgrammeBenefitStatus(b.id));
  top.querySelector('.rc-delete').addEventListener('click', () => deleteProgrammeBenefit(b.id));
  card.appendChild(top);
  /* the measure line — the v8.8 KPI-card field idiom, reused */
  const def = document.createElement('div');
  def.className = 'pb-def';
  const mkField = (field, ph, extraCls) => {
    const el = document.createElement('span');
    el.className = 'kpi-field' + (extraCls ? ' ' + extraCls : '');
    el.contentEditable = 'true'; el.spellcheck = false;
    el.setAttribute('data-ph', ph);
    el.textContent = (typeof b[field] === 'string') ? b[field] : '';
    _onBlurCommit(el, () => {
      const v = (el.textContent || '').trim();
      if (v !== (b[field] || '')) { b[field] = v; markDirty(); }
    });
    return el;
  };
  const lab = (t) => { const s = document.createElement('span'); s.className = 'kpi-k'; s.textContent = t; return s; };
  const seg = (t, cls) => { const s = document.createElement('span'); s.className = cls; s.textContent = t; return s; };
  def.appendChild(lab('Measure'));
  def.appendChild(mkField('measure', 'what is measured, and its unit…'));
  def.appendChild(lab('Base → Target'));
  def.appendChild(mkField('baseline', 'baseline…'));
  def.appendChild(seg('→', 'kpi-bt-arrow'));
  def.appendChild(mkField('target', 'target…'));
  def.appendChild(seg('· now ', 'kpi-now'));
  def.appendChild(mkField('current', 'current…', 'kpi-now'));
  card.appendChild(def);
  const meta = document.createElement('div');
  meta.className = 'rc-meta';
  const ownerField = document.createElement('span'); ownerField.className = 'rc-meta-field';
  ownerField.innerHTML = `Owner: <input type="text" placeholder="(none)" value="${esc(b.owner || '')}">`;
  ownerField.querySelector('input').addEventListener('change', (e) => {
    const v = (e.target.value || '').trim();
    if (v !== (b.owner || '')) { b.owner = v; markDirty(); }
  });
  meta.appendChild(ownerField);
  card.appendChild(meta);
  card.appendChild(_buildBenefitProjectsRow(b));
  return card;
}
function _buildBenefitProjectsRow(b) {
  /* contributing projects = REFERENCES (Q-c, the comms-audience idiom):
     a deleted/archived project's chip silently drops off at render, inert
     ── ⚠⚠ v16.8 (W3): "INERT" WAS FALSE, AND THIS COMMENT IS WHY THE WORST DEFECT IN
     SWEEP-2 EXISTED. A dropped chip is inert only until the routing id is RECLAIMED — create
     a new project with the deleted one's name and `live.find(c => c.id === pid)` succeeds
     again, so this row renders the NEW project as a contributor to the OLD benefit, and the
     benefit reports against work it has nothing to do with. Nothing anywhere says so.
     ⚠⚠ AND THE COMMENT NAMES THE IDIOM IT ONLY HALF-FOLLOWED. The comms-audience pattern is
     two halves: drop the chip at render, AND prune the reference when the target is deleted
     (`deleteStakeholder`, v10.3 W4). This row copied the render half. P4c then measured the
     benefit → project link as the ONLY reference in the app with neither safeguard, which is
     exactly why it was the only one that could silently point at the wrong thing.
     ✅ `deleteProject` now prunes, so the render-time drop is a second line of defence rather
     than the only one — which is what "inert" was assuming all along. */
  const row = document.createElement('div');
  row.className = 'comm-aud-row pb-projects';
  const lbl = document.createElement('span');
  lbl.className = 'comm-aud-lbl';
  lbl.textContent = 'Projects:';
  row.appendChild(lbl);
  const live = _progProjects();   /* v11: a benefit's projects are its own programme's */
  (b.projectIds || []).forEach(pid => {
    const p = live.find(c => c.id === pid);
    if (!p) return;
    const chip = document.createElement('span');
    chip.className = 'comm-aud';
    chip.appendChild(document.createTextNode(p.name || '(unnamed)'));
    const x = document.createElement('button');
    x.type = 'button'; x.title = 'Remove from this benefit'; x.textContent = '×';
    x.addEventListener('click', () => {
      b.projectIds = (b.projectIds || []).filter(v => v !== pid);
      markDirty(); _renderProgrammeBenefits();
    });
    chip.appendChild(x);
    row.appendChild(chip);
  });
  const addable = live.filter(c => !(b.projectIds || []).includes(c.id));
  if (addable.length) {
    const sel = document.createElement('select');
    sel.className = 'comm-aud-add';
    sel.innerHTML = `<option value="">+ Add project…</option>`
      + addable.map(c => `<option value="${esc(String(c.id))}">${esc(c.name || '(unnamed)')}</option>`).join('');
    sel.addEventListener('change', () => {
      const v = sel.value; if (!v) return;
      if (!Array.isArray(b.projectIds)) b.projectIds = [];
      if (!b.projectIds.includes(v)) b.projectIds.push(v);
      markDirty(); _renderProgrammeBenefits();
    });
    row.appendChild(sel);
  }
  return row;
}
/* heatmap row-click → that project's Governance ▸ KPIs, via the existing
   pending-jump machinery (WITHOUT touching _projReturn, so no Focus chip). */
function ovOpenGovernance(projectId) {
  _focusPendingTab = 'changes';
  _focusPendingCard = 'kpiSection';
  gotoProject(projectId);
}
function _ovKpiDotHtml(k, dim) {
  const lvl = (k && k[dim] && ['red', 'amber', 'green'].includes(k[dim].rag)) ? k[dim].rag : null;
  return `<span class="kpi-dot${lvl ? ' kd-' + lvl : ''}"></span>`;
}
function _ovStatusDotHtml(c) {
  const r = c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.rag;
  const lvl = (r && ['red', 'amber', 'green'].includes(r.level)) ? r.level : null;
  return `<span class="kpi-dot${lvl ? ' kd-' + lvl : ''}"></span>`;
}
/* Current gate name for a project, or null when none is set (current === -1).
   Gate names are per-project editable, so the pill shows whatever THAT project
   calls its current gate rather than a fixed vocabulary. */
function _ovGateLabel(c) {
  const g = c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.gates;
  const names = (g && Array.isArray(g.names)) ? g.names : [];
  const cur = (g && typeof g.current === 'number') ? g.current : -1;
  const nm = (cur >= 0 && cur < names.length) ? String(names[cur] || '').trim() : '';
  return nm || null;
}
/* ── v10.4 (W3): ONE consolidated project table ──────────────────────────────────
   The landing page used to render every project TWICE — once as an _ovCard and once
   as a heatmap row — and both showed the same manual Status RAG and the same five
   KPI dots. The cards are gone; this table absorbed the two things only they had
   (the status rationale, and a home for the ⋯ manage menu) and gained Gate and
   Progress.

   Click behaviour is deliberately SPLIT, and both halves are pre-existing machinery:
     row → that project's Overview            (gotoProject — where you actually work)
     dot → that project's Governance ▸ KPIs   (ovOpenGovernance — where KPIs are
                                               actually edited; reads naturally, since
                                               clicking the Cost dot lands on Cost)
   Accepted losses from dropping the cards, recorded so they are not re-litigated:
   next meeting, key action, key risk and the per-card dot echo. All are one click
   away on each project's Overview, and Focus covers cross-project scanning. */
function _ovProjectTableHtml(live, archived, showArchived) {
  const ordered = _listOrderOrdered([...(live || []), ...(archived || [])], 'project', activeProgrammeId);
  const liveOrdered = ordered.filter(function (c) { return !c.archived; });
  const archivedOrdered = ordered.filter(function (c) { return !!c.archived; });
  const shown = showArchived ? liveOrdered.concat(archivedOrdered) : liveOrdered;
  if (!shown.length) return '';
  const manualOrder = _listOrderEntryFor('project', activeProgrammeId, false).mode === 'manual';
  const head = `<tr><th class="pm-th l">Project</th><th class="pm-th">PM</th>`
    + `<th class="pm-th">Gate</th><th class="pm-th">Overall progress</th><th class="pm-th">RAG</th>`
    + KPI_DIMS.map(d => `<th class="pm-th">${_escapeHtml(d[1])}</th>`).join('')
    + `<th class="pm-th pm-th-menu${manualOrder ? ' pm-menu-manual' : ''}"></th></tr>`;
  const rows = shown.map(c => {
    /* ⚠ v13.1 (A56): TWO variables, because this id lands in two different languages.
       `eid` is for plain attributes (data-id, the menu's id); `jid` is for the onclick
       handlers, where it is a JS string literal — see _jsInAttr. Project ids are slugified
       when the app mints them, but an IMPORTED project keeps whatever id its file carried
       (confirmImport only re-mints on a collision), so a hand-edited or hostile import file
       is a real route to an id containing a quote. */
    const eid = esc(String(c.id));
    const jid = _jsInAttr(c.id);
    const k = (c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.kpis && typeof c.appMeta.dashboard.kpis === 'object')
      ? c.appMeta.dashboard.kpis : null;
    const pm = (c.appMeta && typeof c.appMeta.projectManager === 'string' && c.appMeta.projectManager.trim())
      ? c.appMeta.projectManager.trim() : null;
    const why = ((c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.rag
      && c.appMeta.dashboard.rag.rationale) || '').trim();
    const gate = _ovGateLabel(c);
    const progress = _overallProjectProgress(c);
    const pct = progress.percent;
    const dash = '<span class="pm-none">—</span>';
    const govTip = ' KPI — open this project’s Governance ▸ KPIs';
    const dotCell = (html, label) =>
      `<td class="pm-td pm-td-dot" onclick="event.stopPropagation();ovOpenGovernance('${jid}')"`
      + ` title="${_escapeHtml(label)}">${html}</td>`;
    return `<tr class="pm-row${c.archived ? ' pm-row-arch' : ''}" data-id="${eid}"`
      + ` onclick="gotoProject('${jid}')" title="Open ${_escapeHtml(c.name || 'this project')}">`
      /* v16.5 (d): the quotable id sits BEFORE the name, so a column of them lines up and can be
         read down. Absent (rather than a placeholder) on a record that has not been numbered. */
      + `<td class="pm-td l">`
      +   (_dispId('PRJ', c) ? `<span class="disp-id">${_escapeHtml(_dispId('PRJ', c))}</span>` : '')
      +   `<span class="pm-name">${_escapeHtml(c.name || 'Untitled project')}</span>`
      +   _predCountHtml(c)
      +   (why ? `<span class="pm-why">${_escapeHtml(why)}</span>` : '')
      + `</td>`
      + `<td class="pm-td pm-td-pm">${pm ? _escapeHtml(pm) : dash}</td>`
      + `<td class="pm-td">${gate ? `<span class="pm-gate">${_escapeHtml(gate)}</span>` : dash}</td>`
      + `<td class="pm-td pm-progress"><span class="pm-progress-main">`
      +   `<span class="pm-bar" aria-hidden="true"><i style="width:${pct === null ? 0 : pct}%"></i></span>`
      +   `<span class="pm-pct">${_progressValueText(pct)}</span></span>`
      +   `<span class="pm-progress-meta">`
      +     (progress.source === 'assessment' ? `<span class="progress-assessed">Assessed</span>` : '')
      +     _progressHelpButtonHtml(c)
      +   `</span></td>`
      + dotCell(_ovStatusDotHtml(c), 'Overall status — open this project’s Governance ▸ KPIs')
      + KPI_DIMS.map(d => dotCell(_ovKpiDotHtml(k, d[0]), d[1] + govTip)).join('')
      + `<td class="pm-td pm-td-menu${manualOrder ? ' pm-menu-manual' : ''}">`
      +   _listOrderMoveHtml('project', activeProgrammeId, c)
      +   `<div class="pm-menu-wrap">`
      +   `<button class="ov-card-menu-btn pm-menu-btn" data-id="${eid}" onclick="toggleCardMenu(event, this.dataset.id)" title="Manage project" aria-label="Manage project">`
      +     `<svg viewBox="0 0 16 16" width="15" height="15"><circle cx="3" cy="8" r="1.45"/><circle cx="8" cy="8" r="1.45"/><circle cx="13" cy="8" r="1.45"/></svg>`
      +   `</button><div class="ov-card-menu" id="cardMenu-${eid}" hidden></div>`
      + `</div></td>`
      + `</tr>`;
  }).join('');
  return `<div class="ov-heat"><table class="pm-table"><thead>${head}</thead><tbody>${rows}</tbody></table></div>`;
}

/* ── Overview cards: per-project rollups. These read each project's OWN data
   object (c.actions / c.edits / c.risks / c.workstreams / c.appMeta), never the
   module globals, so every card is correct regardless of which project is active. ── */
/* PROGRESS rollup — pooled / action-weighted across the WBS.
   ── v10.4 (W3): renamed from _ovSuccessPct. "Success" was account-management
   vocabulary that outlived account management by ten versions: v10.1's Q9 renamed
   SUCCESS_PROJECTS → WORKSTREAMS but missed this function and its visible "Success"
   label. Build standard 4 — rename with the concept, not afterwards.

   What the number means, stated plainly because it is easy to over-trust:
     - only actions ASSIGNED to a WBS element count; a free-floating action is invisible
     - Completed = 100 regardless of its progress field; Cancelled is excluded
     - anything else contributes edits[id].progress, or 0 if never set
     - a plain per-action average, UNWEIGHTED: ten actions under one workstream outweigh
       one under another, and tree depth makes no difference
     - an action linked under two elements counts once
   So it flatters when WBS coverage is patchy — the Scope ▸ coverage strip is the
   existing check for that. No effort weighting or tree roll-up is intended.

   ⚠ v10.4 also fixes a real blind spot: this read c.actions ONLY, so a just-added,
   unsaved action was invisible to it — the same family as the v10.3 P0 and the id
   counter. Build standard 7: newActions is part of the action set. */
function _projectActionProgressSummary(c) {
  const workstreams = c && Array.isArray(c.workstreams) ? c.workstreams : [];
  const pool = [...(c && Array.isArray(c.actions) ? c.actions : []),
                ...(c && Array.isArray(c.newActions) ? c.newActions : [])];
  const deleted = c && c._deletedIds instanceof Set ? c._deletedIds : new Set();
  const byId = new Map();
  pool.forEach(function (a) {
    if (a && !a.del && !deleted.has(a.id)) byId.set(a.id, a);
  });
  const ed = (c && c.edits && typeof c.edits === 'object' && !Array.isArray(c.edits)) ? c.edits : {};
  const seen = new Set();
  let sum = 0, count = 0;
  workstreams.forEach(function (w) {
    (w && Array.isArray(w.actionIds) ? w.actionIds : []).forEach(function (id) {
      if (seen.has(id)) return;
      seen.add(id);
      const a = byId.get(id); if (!a) return;
      const e = ed[id] || {};
      const status = e.status !== undefined
        ? e.status
        : ((typeof STATUS_DISPLAY === 'object' && STATUS_DISPLAY[a.status]) || a.status);
      if (status === 'Cancelled') return;
      const progress = typeof e.progress === 'number'
        ? e.progress
        : (typeof a.progress === 'number' ? a.progress : 0);
      sum += status === 'Completed' ? 100 : progress;
      count += 1;
    });
  });
  return { percent:count ? Math.round(sum / count) : null, count:count };
}
function _ovProgressPct(c) { return _projectActionProgressSummary(c).percent; }
function _overallProjectProgress(c) {
  const action = _projectActionProgressSummary(c);
  const holder = _progressAssessmentHolder(c);
  if (!holder || !Object.prototype.hasOwnProperty.call(holder, 'progressAssessment')) {
    return { percent:action.percent, source:'actions', actionPercent:action.percent,
      actionCount:action.count, note:'', assessedAt:'', invalidAssessment:false };
  }
  const saved = holder.progressAssessment;
  const valid = !!(saved && typeof saved === 'object' && !Array.isArray(saved)
    && Number.isInteger(saved.percent) && saved.percent >= 0 && saved.percent <= 100);
  return {
    percent:valid ? saved.percent : null,
    source:'assessment',
    actionPercent:action.percent,
    actionCount:action.count,
    note:valid && typeof saved.note === 'string' ? saved.note : '',
    assessedAt:valid && typeof saved.assessedAt === 'string' ? saved.assessedAt : '',
    invalidAssessment:!valid
  };
}
/* Key Risk — top open THREAT by score (probability × impact, via _riskScoreInfo);
   closed risks + opportunities excluded. Tie-break: highest score → higher impact →
   lowest id (oldest). plus = how many OTHER open threats share the top score (→ "+N"). */
/* Key Action — the crosshair singleton (appMeta.focusActionId). */
/* Next meeting — latest session's next-meeting fields (additive; populated from 1g
   onward, empty before that).
   ⚠ v14 (W4b): this used to end "sessions[0] = newest", which is no longer true and is now the
   opposite of the truth — the merge sorts sessions by id ascending, so index 0 is the OLDEST.
   "Latest" has one definition in this shell: _mostRecentSession(). */
/* show-archived toggle state (survives renderOverview rebuilds, which wipe the DOM). */
let _ovShowArchived = false;

